import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@c2fo/liquidity';
import { ArrowsRotateLightIcon, CalendarCheckLightIcon, CalendarLinesLightIcon } from '@c2fo/liquidity/icons';
import { RecurringRule } from '@/enterprise/data/useRecurringRules';
import { OfferSchedule, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import {
  buildCadenceFromExistingOfferSchedule,
  buildCustomScheduleSentenceFromFormData,
} from '@/enterprise/features/offerModal/utils';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import useRuleDescription from '@/enterprise/features/recurringRules/utils/useRuleDescription';

type DivisionDetailsChipConfig = {
  [key in 'oneTimeOfferOn' | 'recurringOfferOn' | 'futureOffer' | 'invoiceRules' | 'offerSchedule']: {
    label: React.ReactNode;
    className: string;
    shouldShow: boolean;
    tooltip?: React.ReactNode;
  };
};

interface DivisionDetailsChipsProps {
  displayTooltip?: boolean;
  size?: 'xs' | 'sm';
  takerMarket: TakerMarket;
}

const RuleDescription = ({ rule }: { rule: RecurringRule }) => {
  const { t } = useTranslation();
  const { daysPaidEarly, dueDate, invoiceAmount, excludedInvoiceIds } = useRuleDescription({
    currency: rule.currency,
    rule: rule,
  });

  const ruleCategories = [
    { label: t('recurringRules.dpe'), description: daysPaidEarly.short, key: 'dpe' },
    { label: t('recurringRules.invoiceAmount'), description: invoiceAmount.short, key: 'invoiceAmount' },
    { label: t('recurringRules.dueDate'), description: dueDate.short, key: 'dueDate' },
    { label: t('recurringRules.invoiceId'), description: excludedInvoiceIds.short, key: 'excludedInvoiceIds' },
  ].filter((category) => category.description);

  return (
    <div>
      {ruleCategories.map((category) => (
        <div key={category.key} className="flex items-start gap-1">
          <span className="capitalize">{category.label}:</span>
          <span>{category.description}</span>
        </div>
      ))}
    </div>
  );
};

const OfferScheduleDescription = ({ offerSchedule }: { offerSchedule: OfferSchedule }) => {
  const { t } = useTranslation();
  const { startDate, endDate, schedule } = offerSchedule;
  const cadence = buildCadenceFromExistingOfferSchedule(schedule);

  return buildCustomScheduleSentenceFromFormData({ startDate, endDate, cadence, t });
};

const TooltipContent = ({ title, children }: { title?: string; children: React.ReactNode }) => {
  return (
    <div className="space-y-1 text-sm">
      {title && <div className="font-medium">{title}</div>}
      <div>{children}</div>
    </div>
  );
};

const InvoiceRulesTooltip = ({ rules }: { rules: RecurringRule[] }) => (
  <TooltipContent>
    {rules.map((rule) => (
      <div key={rule.id}>
        <RuleDescription rule={rule} />
      </div>
    ))}
  </TooltipContent>
);

const OfferScheduleTooltip = ({ offerSchedule }: { offerSchedule: OfferSchedule }) => (
  <TooltipContent>
    <OfferScheduleDescription offerSchedule={offerSchedule} />
  </TooltipContent>
);

export const useDivisionDetailsChips = (takerMarket: TakerMarket) => {
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const recurringRules = getRecurringRulesForTakerMarkets([takerMarket]);
  const { offerSchedule } = takerMarket;
  const hasOfferSchedule = !!offerSchedule;
  const hasRecurringRules = recurringRules.length > 0;
  const marketIsEnabled = takerMarket.marketIsEnabled === true;
  const offerConfigIsEnabled = takerMarket.offerConfig?.isEnabled === true;
  const offerFrequency = takerMarket.offerConfig?.frequency;
  const hasOneTimeOfferOn =
    marketIsEnabled && offerConfigIsEnabled && offerFrequency === 'ONE_TIME' && !hasOfferSchedule;
  const hasRecurringOfferOn =
    marketIsEnabled && offerConfigIsEnabled && offerFrequency === 'RECURRING' && !hasOfferSchedule;
  const hasFutureOffer = !marketIsEnabled && offerConfigIsEnabled;
  const hasActiveOffer = hasOneTimeOfferOn || hasRecurringOfferOn || hasOfferSchedule;
  const hasAnyChip = hasActiveOffer || hasFutureOffer || hasRecurringRules;

  return {
    hasAnyChip,
    hasActiveOffer,
    recurringRules,
    offerSchedule,
    marketIsEnabled,
    offerConfigIsEnabled,
    offerFrequency,
  };
};

const DivisionDetailsChips = ({ displayTooltip = true, size = 'sm', takerMarket }: DivisionDetailsChipsProps) => {
  const { t } = useTranslation();
  const { recurringRules, offerSchedule, marketIsEnabled, offerConfigIsEnabled, offerFrequency } =
    useDivisionDetailsChips(takerMarket);
  const hasOfferSchedule = !!offerSchedule;
  const hasRecurringRules = recurringRules.length > 0;

  const divisionDetailsChipConfig: DivisionDetailsChipConfig = {
    oneTimeOfferOn: {
      label: (
        <div className="flex items-center gap-1">
          <CalendarCheckLightIcon className="size-5 fill-indigo-900" />
          {t('builder.activeOffer')}
        </div>
      ),
      className: 'bg-indigo-100 text-indigo-900',
      shouldShow: marketIsEnabled && offerConfigIsEnabled && offerFrequency === 'ONE_TIME' && !hasOfferSchedule,
    },
    recurringOfferOn: {
      label: (
        <div className="flex items-center gap-1">
          <ArrowsRotateLightIcon className="size-5 fill-indigo-900" />
          {t('builder.activeOffer')}
        </div>
      ),
      className: 'bg-indigo-100 text-indigo-900',
      shouldShow: marketIsEnabled && offerConfigIsEnabled && offerFrequency === 'RECURRING' && !hasOfferSchedule,
    },
    offerSchedule: {
      label: (
        <div className="flex items-center gap-1">
          <CalendarLinesLightIcon className="size-5 fill-indigo-900" />
          {marketIsEnabled ? t('builder.activeOffer') : t('automation.offerSchedule')}
        </div>
      ),
      className: 'bg-indigo-100 text-indigo-900',
      shouldShow: hasOfferSchedule,
      tooltip: offerSchedule && <OfferScheduleTooltip offerSchedule={offerSchedule} />,
    },
    futureOffer: {
      label: t('builder.futureOffer'),
      className: 'bg-gray-200 text-gray-900',
      shouldShow: !marketIsEnabled && offerConfigIsEnabled,
    },
    invoiceRules: {
      label: t('automation.rulesApplied'),
      className: 'bg-lightBlue-100 text-lightBlue-900',
      shouldShow: hasRecurringRules,
      tooltip: <InvoiceRulesTooltip rules={recurringRules} />,
    },
  };

  const chipsToShow = Object.entries(divisionDetailsChipConfig).filter(([_, config]) => config.shouldShow);

  if (chipsToShow.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-1 sm:gap-2">
      {chipsToShow.map(([key, config]) => {
        const chipElement = (
          <Chip key={key} label={config.label} className={config.className} variant="standard" size={size} />
        );

        // Conditionally wrap in Tooltip if the chip has a tooltip
        return config.tooltip && displayTooltip ? (
          <Tooltip key={key}>
            <Tooltip.Trigger>{chipElement}</Tooltip.Trigger>
            <Tooltip.Content arrow collisionPadding={24} side="top" variant="dark" className="max-w-sm sm:max-w-xl">
              {config.tooltip}
            </Tooltip.Content>
          </Tooltip>
        ) : (
          chipElement
        );
      })}
    </div>
  );
};

export default DivisionDetailsChips;
